import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  AddLeaveAdjustments,
  FetchLeaveAdjestment,
} from '../../services/leaveadjuestmentservice';

const getErrorMessage = (error) =>
  (error?.response && error.response.data &&
    (error.response.data.Message || error.response.data.message)) ||
  error?.message ||
  String(error);

  
const resolveEmpServiceNo = (passed) => {
  if (passed) return passed;

  const stored = localStorage.getItem('ADU_EmployeeId');
  if (!stored) throw new Error('Employee information not found in localStorage');

  let data;
  try {
    data = JSON.parse(stored);
  } catch {
    data = null;
  }

  const id = data?.ADU_EmployeeId || data?.raw?.ADU_EmployeeId || data?.serviceNo;
  if (!id) throw new Error('Employee ID not found');
  return id;
};

/* ── Thunks ──────────────────────────────────────────────────────────────── */

export const fetchLeaveAdjustments = createAsyncThunk(
  'leaveAdjustment/fetchAll',
  async (empServiceNo, { rejectWithValue }) => {
    try {
      const finalEmpNo = resolveEmpServiceNo(empServiceNo);
      return await FetchLeaveAdjestment(finalEmpNo);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);


export const addLeaveAdjustment = createAsyncThunk(
  'leaveAdjustment/add',
  async ({
    empServiceNo,
    leaveType,
    adjustmentDate,
    days,
    adjustmentType,
    reason,
    createdBy,
  }) => {
    try {
      const finalCreatedBy = resolveEmpServiceNo(createdBy);

      const response = await AddLeaveAdjustments(
        empServiceNo,
        leaveType,
        adjustmentDate,
        days,
        adjustmentType,
        reason,
        finalCreatedBy
      );

      return {
        success: true,
        data: (response && response.data) || response,
        message: 'Leave adjustment added successfully',
        empServiceNo,
      };
    } catch (error) {
      const message = getErrorMessage(error);
      console.error('[leaveAdjustment] add failed:', message);
      return { success: false, message, error };
    }
  }
);

/* ── Slice ───────────────────────────────────────────────────────────────── */

const initialState = {
  items: [],
  loading: false,
  saving: false,
  error: null,
};

const leaveAdjustmentSlice = createSlice({
  name: 'leaveAdjustment',
  initialState,
  reducers: {
    clearLeaveAdjustmentError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeaveAdjustments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeaveAdjustments.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
      })
      .addCase(fetchLeaveAdjustments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load adjustments';
      })

      .addCase(addLeaveAdjustment.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(addLeaveAdjustment.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload && action.payload.success === false) {
          state.error = action.payload.message || 'Failed to add adjustment';
        }
      })
      .addCase(addLeaveAdjustment.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error?.message || 'Failed to add adjustment';
      });
  },
});

export const { clearLeaveAdjustmentError } = leaveAdjustmentSlice.actions;

export const selectLeaveAdjustments = (state) => state.leaveAdjustment.items;
export const selectLeaveAdjustmentLoading = (state) => state.leaveAdjustment.loading;
export const selectLeaveAdjustmentSaving = (state) => state.leaveAdjustment.saving;
export const selectLeaveAdjustmentError = (state) => state.leaveAdjustment.error;

export default leaveAdjustmentSlice.reducer;